import type { AppTheme, ChatMessage, PersistedState } from "../types";

const THEMES: AppTheme[] = ["rose", "peach", "lilac", "sage"];

/** Themes from before the redesign (pearl, blue, amber) fall back to rose. */
export function normalizeTheme(value: unknown): AppTheme {
  return THEMES.includes(value as AppTheme) ? value as AppTheme : "rose";
}

const STORAGE_KEY = "pearlie.web.state.v1";
/** Where the web app kept everything before it was called Pearlie. */
const LEGACY_STORAGE_KEY = "brother.web.state.v1";

export const defaultState = (): PersistedState => ({
  version: 1,
  introSeen: false,
  profile: null,
  messages: [],
  notebook: null,
  lastMessageAt: null,
  draft: "",
  consentVersion: 0,
  theme: "rose",
  displayMode: "light",
  gravitySessionID: `pearlie-web-${crypto.randomUUID()}`,
  signInNudgeShown: false,
  successfulReplyCount: 0,
  researchSharingEnabled: false,
  researchChoiceMade: false,
  researchConversationID: crypto.randomUUID(),
  researchDeletionKey: crypto.randomUUID(),
});

/** Replies saved before the rename carry the old role name. */
function relabel(messages: unknown): ChatMessage[] {
  if (!Array.isArray(messages)) return [];
  return (messages as ChatMessage[]).slice(-1_000).map(message =>
    message.role === "user" || message.role === "pearlie" ? message : { ...message, role: "pearlie" as const });
}

export function loadState(): PersistedState {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    const legacy = raw ? null : localStorage.getItem(LEGACY_STORAGE_KEY);
    raw ??= legacy;
    if (!raw) return defaultState();
    const value = JSON.parse(raw) as Partial<PersistedState>;
    if (value.version !== 1) return defaultState();
    const state: PersistedState = {
      ...defaultState(),
      ...value,
      theme: normalizeTheme(value.theme),
      profile: value.profile ? { ...value.profile, selectedTheme: normalizeTheme(value.profile.selectedTheme) } : null,
      messages: relabel(value.messages),
      notebook: value.notebook ?? null,
    };
    if (legacy) {
      // Move it to the new key once; the old copy goes only after that works.
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch {
        // Full or blocked storage: keep the old copy and try again next load.
      }
    }
    return state;
  } catch {
    return defaultState();
  }
}

export function saveState(state: PersistedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Browser storage can be unavailable in strict private modes. The current
    // session still works; the UI explains persistence in Settings.
  }
}

export function clearState(): PersistedState {
  const reset = defaultState();
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
  return reset;
}
